/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { UnitPositionType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface UnitPositionSummaryProps {
  id: string;
  name: string;
  type: UnitPositionType;
  isFlightCrew: boolean;
  isNonEmployee: boolean;
  notificationAddress: string | null;
  teamMemberId: string | null;
  teamMemberFirstName: string | null;
  teamMemberLastName: string | null;
  isNonCompliant: boolean;
  nonComplianceReason: string | null;
}

export class UnitPositionSummaryRecord extends Record<UnitPositionSummaryProps>({
  id: null as any,
  name: null as any,
  type: null as any,
  isFlightCrew: null as any,
  isNonEmployee: null as any,
  notificationAddress: null as any,
  teamMemberId: null as any,
  teamMemberFirstName: null as any,
  teamMemberLastName: null as any,
  isNonCompliant: null as any,
  nonComplianceReason: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    type: UnitPositionType;
    isFlightCrew: boolean;
    isNonEmployee: boolean;
    notificationAddress: string | null;
    teamMemberId: string | null;
    teamMemberFirstName: string | null;
    teamMemberLastName: string | null;
    isNonCompliant: boolean;
    nonComplianceReason: string | null;
  }): UnitPositionSummaryRecord {
    if (json == null) {
      throw new Error('UnitPositionSummaryRecord.fromJS: parameter cannot be null');
    }
    return new UnitPositionSummaryRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'UnitPositionSummaryJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'UnitPositionSummaryJson.name'),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', UnitPositionType.Pilot),
        'UnitPositionSummaryJson.type',
      ),
      isFlightCrew: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isFlightCrew', false),
        'UnitPositionSummaryJson.isFlightCrew',
      ),
      isNonEmployee: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isNonEmployee', false),
        'UnitPositionSummaryJson.isNonEmployee',
      ),
      notificationAddress: json.notificationAddress,
      teamMemberId: json.teamMemberId,
      teamMemberFirstName: json.teamMemberFirstName,
      teamMemberLastName: json.teamMemberLastName,
      isNonCompliant: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isNonCompliant', false),
        'UnitPositionSummaryJson.isNonCompliant',
      ),
      nonComplianceReason: json.nonComplianceReason,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    type: UnitPositionType;
    isFlightCrew: boolean;
    isNonEmployee: boolean;
    notificationAddress: string | null;
    teamMemberId: string | null;
    teamMemberFirstName: string | null;
    teamMemberLastName: string | null;
    isNonCompliant: boolean;
    nonComplianceReason: string | null;
  }): [UnitPositionSummaryRecord, Error[]] {
    if (json == null) {
      throw new Error('UnitPositionSummaryRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new UnitPositionSummaryRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'UnitPositionSummaryJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'UnitPositionSummaryJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', UnitPositionType.Pilot),
            'UnitPositionSummaryJson.type',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isFlightCrew: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isFlightCrew', false),
            'UnitPositionSummaryJson.isFlightCrew',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isNonEmployee: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isNonEmployee', false),
            'UnitPositionSummaryJson.isNonEmployee',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notificationAddress: tryOrDefault(
        () => json.notificationAddress,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      teamMemberId: tryOrDefault(
        () => json.teamMemberId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      teamMemberFirstName: tryOrDefault(
        () => json.teamMemberFirstName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      teamMemberLastName: tryOrDefault(
        () => json.teamMemberLastName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isNonCompliant: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isNonCompliant', false),
            'UnitPositionSummaryJson.isNonCompliant',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      nonComplianceReason: tryOrDefault(
        () => json.nonComplianceReason,
        (err) => errors.push(err),
      )!,
    });

    return [record, errors];
  }

  constructor(props: UnitPositionSummaryProps) {
    super(props);
    if (props == null) {
      throw new Error('UnitPositionSummaryRecord constructor parameter cannot be null');
    }
  }
}

export abstract class UnitPositionSummaryViewModelBase implements UnitPositionSummaryProps {
  protected readonly _props: UnitPositionSummaryProps;
  private _json?: UnitPositionSummaryProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get type(): UnitPositionType {
    return this._props.type;
  }
  get isFlightCrew(): boolean {
    return this._props.isFlightCrew;
  }
  get isNonEmployee(): boolean {
    return this._props.isNonEmployee;
  }
  get notificationAddress(): string | null {
    return this._props.notificationAddress;
  }
  get teamMemberId(): string | null {
    return this._props.teamMemberId;
  }
  get teamMemberFirstName(): string | null {
    return this._props.teamMemberFirstName;
  }
  get teamMemberLastName(): string | null {
    return this._props.teamMemberLastName;
  }
  get isNonCompliant(): boolean {
    return this._props.isNonCompliant;
  }
  get nonComplianceReason(): string | null {
    return this._props.nonComplianceReason;
  }

  public get isClinician() {
    return this._props.type === UnitPositionType.Clinician;
  }
  public get isClinicianTrainee() {
    return this._props.type === UnitPositionType.ClinicianTrainee;
  }
  public get isCoPilot() {
    return this._props.type === UnitPositionType.CoPilot;
  }
  public get isDriver() {
    return this._props.type === UnitPositionType.Driver;
  }
  public get isMechanic() {
    return this._props.type === UnitPositionType.Mechanic;
  }
  public get isNurse() {
    return this._props.type === UnitPositionType.Nurse;
  }
  public get isObserver() {
    return this._props.type === UnitPositionType.Observer;
  }
  public get isOther() {
    return this._props.type === UnitPositionType.Other;
  }
  public get isParamedic() {
    return this._props.type === UnitPositionType.Paramedic;
  }
  public get isPilot() {
    return this._props.type === UnitPositionType.Pilot;
  }
  public get isPilotTrainee() {
    return this._props.type === UnitPositionType.PilotTrainee;
  }

  public isAnyType(...unitPositionType: UnitPositionType[]) {
    return unitPositionType.includes(this._props.type);
  }

  constructor(props: UnitPositionSummaryProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        type: this._props.type,
        isFlightCrew: this._props.isFlightCrew,
        isNonEmployee: this._props.isNonEmployee,
        notificationAddress: this._props.notificationAddress,
        teamMemberId: this._props.teamMemberId,
        teamMemberFirstName: this._props.teamMemberFirstName,
        teamMemberLastName: this._props.teamMemberLastName,
        isNonCompliant: this._props.isNonCompliant,
        nonComplianceReason: this._props.nonComplianceReason,
      };
    }
    return this._json;
  }
}
